import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    Globe, Trophy, GraduationCap, Shield, Clock, Search, ChevronDown, Info,
    Plus, ClipboardPaste, Pencil, Trash2, X, Users, ExternalLink,
} from "lucide-react";
import { LEAGUES } from "../lib/leagues";
import { CATEGORY_META, CATEGORY_ORDER } from "../lib/leagueDirectory";
import { useAuth } from "../lib/AuthContext";
import { useAdminMode } from "../lib/AdminModeContext";
import { useLeague } from "../context/LeagueContext";
import { FindCascade } from "../components/Layout";
import {
    getLeaguesDirectory, getLeagueTeams, createLeague, updateLeague, deleteLeague,
    bulkAddLeagues, reorderLeagues, addLeagueTeam, bulkAddLeagueTeams, deleteLeagueTeam,
} from "../lib/api";

const CAT_ICON = { professional: Shield, college: GraduationCap, international: Globe, historical: Clock };
const inp = "bg-[#0E0F16] border border-white/10 rounded-lg text-white text-sm outline-none focus:border-[#9D4CDD] transition-colors px-3 py-2";

// Leagues that have a dedicated in-app page.
const LEAGUE_ROUTE = { pwhl: "/teams", ncaa: "/ncaa", olympics: "/olympics" };
const routeFor = (lg) => LEAGUE_ROUTE[lg.id] || (LEAGUES[lg.id] ? `/leagues/${lg.id}` : null);

function monogram(abbr) {
    return (abbr || "").replace(/[^A-Za-z0-9]/g, "").slice(0, 4).toUpperCase();
}

/* ---------- Modal shell ---------- */
function Modal({ title, onClose, children, testId, wide }) {
    return (
        <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 overflow-y-auto bg-black/70 backdrop-blur-sm" data-testid={testId}>
            <div className={`relative w-full ${wide ? "max-w-2xl" : "max-w-lg"} mt-16 rounded-2xl border border-white/10 bg-[#0B0C12] shadow-2xl`}>
                <div className="flex items-center gap-3 px-5 py-4 border-b border-white/[0.06]">
                    <h3 className="font-heading text-lg font-black text-white tracking-tight">{title}</h3>
                    <button onClick={onClose} data-testid="modal-close" className="ml-auto h-8 w-8 rounded-lg flex items-center justify-center text-white/50 hover:text-white hover:bg-white/5 transition-colors">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}

/* ---------- League tile ---------- */
function LeagueTile({ lg, adminOn, onOpen, dragProps }) {
    const dest = routeFor(lg);
    const color = lg.color || CATEGORY_META[lg.category]?.color || "#9D4CDD";
    const inner = (
        <>
            <div className="h-11 w-11 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}1f`, border: `1px solid ${color}55` }}>
                <span className="font-black leading-none" style={{ color, fontSize: monogram(lg.abbr).length > 3 ? "9px" : "12px" }}>{monogram(lg.abbr)}</span>
            </div>
            <span className="text-[11px] font-bold text-white/85 leading-tight text-center line-clamp-1 w-full">{lg.abbr}</span>
        </>
    );
    const cls = `group rounded-xl border border-white/[0.06] bg-[#0E0F16] hover:bg-[#17181f] transition-colors p-3 flex flex-col items-center justify-center gap-2 h-[104px] w-full ${adminOn ? "cursor-move ring-1 ring-[#9D4CDD]/20" : ""}`;
    // Admin: drag to reorder + click opens manage panel. Public: link to the
    // league's page when one exists, else open a read-only teams view.
    if (adminOn) {
        return <button onClick={() => onOpen(lg)} data-testid={`league-tile-${lg.id}`} title={lg.name} className={cls} {...dragProps}>{inner}</button>;
    }
    if (dest) {
        return <Link to={dest} data-testid={`league-tile-${lg.id}`} title={lg.name} className={cls}>{inner}</Link>;
    }
    return <button onClick={() => onOpen(lg)} data-testid={`league-tile-${lg.id}`} title={lg.name} className={cls}>{inner}</button>;
}

function Section({ cat, leagues, open, onToggle, adminOn, onOpen, dragId, onDragStart, onDrop }) {
    const meta = CATEGORY_META[cat];
    const Icon = CAT_ICON[cat];
    return (
        <div className="rounded-2xl bg-[#0B0C12] border border-white/[0.06] overflow-hidden" data-testid={`league-section-${cat}`}>
            <button onClick={onToggle} className="w-full flex items-center gap-3 px-5 py-4 hover:bg-white/[0.02] transition-colors">
                <span className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${meta.color}1f`, color: meta.color }}>
                    <Icon className="h-4 w-4" strokeWidth={2} />
                </span>
                <div className="text-left min-w-0">
                    <p className="text-sm font-black uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</p>
                    <p className="text-[11px] text-white/45 truncate">{meta.desc}</p>
                </div>
                <div className="ml-auto flex items-center gap-3 shrink-0">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white/50 bg-white/[0.04] border border-white/10 rounded-full px-2.5 py-1">{leagues.length} Leagues</span>
                    <ChevronDown className={`h-4 w-4 text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
                </div>
            </button>
            {open && leagues.length > 0 && (
                <div className="px-5 pb-5 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 xl:grid-cols-9 gap-3">
                    {leagues.map((lg) => (
                        <LeagueTile key={lg.id} lg={lg} adminOn={adminOn} onOpen={onOpen}
                            dragProps={adminOn ? {
                                draggable: true,
                                onDragStart: () => onDragStart(lg.id),
                                onDragOver: (e) => e.preventDefault(),
                                onDrop: (e) => { e.preventDefault(); onDrop(lg.id); },
                            } : undefined} />
                    ))}
                </div>
            )}
            {open && leagues.length === 0 && <p className="px-5 pb-5 text-xs text-white/35">No leagues match your search.</p>}
        </div>
    );
}

function StatCard({ Icon, value, label, sub }) {
    return (
        <div className="flex items-center gap-4 rounded-2xl bg-[#0B0C12] border border-white/[0.06] px-5 py-4">
            <span className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0 bg-[#9D4CDD]/12 text-[#C4A5F4]"><Icon className="h-5 w-5" strokeWidth={2} /></span>
            <div className="min-w-0">
                <p className="text-2xl font-black text-white leading-none">{value}</p>
                <p className="text-[12px] font-bold text-white/80 mt-1 leading-none">{label}</p>
                <p className="text-[11px] text-white/40 mt-0.5 leading-none">{sub}</p>
            </div>
        </div>
    );
}

/* ---------- Bulk-paste leagues ---------- */
function BulkLeaguesModal({ onClose }) {
    const qc = useQueryClient();
    const [tsv, setTsv] = useState("");
    const [hasHeader, setHasHeader] = useState(false);
    const [defCat, setDefCat] = useState("professional");
    const m = useMutation({
        mutationFn: () => bulkAddLeagues({ tsv, has_header: hasHeader, default_category: defCat }),
        onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["leagues-directory"] }); toast.success(`Added ${r.added} league${r.added === 1 ? "" : "s"}`); onClose(); },
        onError: () => toast.error("Bulk add failed"),
    });
    return (
        <Modal title="Bulk add leagues" onClose={onClose} testId="bulk-leagues-modal" wide>
            <p className="text-[12px] text-white/50 mb-3">Paste rows straight from Google Sheets (tab-separated). Columns: <span className="text-white/75 font-semibold">name · abbr · category · country · region · level · link</span>. Only the first column (name) is required.</p>
            <textarea value={tsv} onChange={(e) => setTsv(e.target.value)} rows={8} placeholder={"PWHL\tPWHL\tprofessional\tCanada\nSDHL\tSDHL\tprofessional\tSweden"} data-testid="bulk-leagues-tsv" className={`${inp} w-full font-mono text-xs resize-y`} />
            <div className="flex flex-wrap items-center gap-4 mt-3">
                <label className="flex items-center gap-2 text-xs text-white/70"><input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} data-testid="bulk-leagues-header" /> First row is a header</label>
                <label className="flex items-center gap-2 text-xs text-white/70">Default category
                    <select value={defCat} onChange={(e) => setDefCat(e.target.value)} data-testid="bulk-leagues-defcat" className={inp}>
                        {CATEGORY_ORDER.map((c) => <option key={c} value={c}>{CATEGORY_META[c].label}</option>)}
                    </select>
                </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
                <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-white/60 hover:text-white">Cancel</button>
                <button onClick={() => m.mutate()} disabled={!tsv.trim() || m.isPending} data-testid="bulk-leagues-submit" className="px-4 py-2 rounded-lg text-sm font-bold bg-[#9D4CDD] text-white hover:bg-[#8B3FC9] disabled:opacity-40">{m.isPending ? "Adding…" : "Add leagues"}</button>
            </div>
        </Modal>
    );
}

/* ---------- Add / edit single league ---------- */
function LeagueEditModal({ league, onClose }) {
    const qc = useQueryClient();
    const editing = !!league;
    const [f, setF] = useState({
        abbr: league?.abbr || "", name: league?.name || "", category: league?.category || "professional",
        country: league?.country || "", region: league?.region || "", level: league?.level || "", link: league?.link || "",
    });
    const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
    const m = useMutation({
        mutationFn: () => (editing ? updateLeague(league.id, f) : createLeague(f)),
        onSuccess: () => { qc.invalidateQueries({ queryKey: ["leagues-directory"] }); toast.success(editing ? "League updated" : "League added"); onClose(); },
        onError: () => toast.error("Save failed"),
    });
    const del = useMutation({
        mutationFn: () => deleteLeague(league.id),
        onSuccess: () => { qc.invalidateQueries({ queryKey: ["leagues-directory"] }); toast.success("League deleted"); onClose(); },
        onError: () => toast.error("Delete failed"),
    });
    const field = (label, k, ph) => (
        <label className="block">
            <span className="text-[11px] uppercase tracking-wider font-bold text-white/45">{label}</span>
            <input value={f[k]} onChange={set(k)} placeholder={ph} data-testid={`league-field-${k}`} className={`${inp} w-full mt-1`} />
        </label>
    );
    return (
        <Modal title={editing ? `Edit ${league.abbr}` : "Add league"} onClose={onClose} testId="league-edit-modal">
            <div className="grid grid-cols-2 gap-3">
                {field("Abbreviation", "abbr", "PWHL")}
                {field("Full name", "name", "Professional Women's Hockey League")}
                <label className="block">
                    <span className="text-[11px] uppercase tracking-wider font-bold text-white/45">Category</span>
                    <select value={f.category} onChange={set("category")} data-testid="league-field-category" className={`${inp} w-full mt-1`}>
                        {CATEGORY_ORDER.map((c) => <option key={c} value={c}>{CATEGORY_META[c].label}</option>)}
                    </select>
                </label>
                {field("Country", "country", "Canada")}
                {field("Region", "region", "North America")}
                {field("Level", "level", "Professional")}
                <div className="col-span-2">{field("Link (official site)", "link", "https://…")}</div>
            </div>
            <div className="flex items-center gap-2 mt-5">
                {editing && <button onClick={() => del.mutate()} data-testid="league-delete-btn" className="px-3 py-2 rounded-lg text-sm font-bold text-red-400 hover:bg-red-500/10 inline-flex items-center gap-1.5"><Trash2 className="h-4 w-4" /> Delete</button>}
                <button onClick={onClose} className="ml-auto px-4 py-2 rounded-lg text-sm text-white/60 hover:text-white">Cancel</button>
                <button onClick={() => m.mutate()} disabled={m.isPending || (!f.abbr.trim() && !f.name.trim())} data-testid="league-save-btn" className="px-4 py-2 rounded-lg text-sm font-bold bg-[#9D4CDD] text-white hover:bg-[#8B3FC9] disabled:opacity-40">{m.isPending ? "Saving…" : "Save"}</button>
            </div>
        </Modal>
    );
}

/* ---------- League panel: teams (view + admin manage) ---------- */
function LeaguePanel({ league, adminOn, onEdit, onClose }) {
    const qc = useQueryClient();
    const { data: teams = [], isLoading } = useQuery({ queryKey: ["league-teams", league.id], queryFn: () => getLeagueTeams(league.id) });
    const [mode, setMode] = useState(null); // "one" | "bulk"
    const [one, setOne] = useState({ name: "", abbr: "", city: "" });
    const [tsv, setTsv] = useState("");
    const [hasHeader, setHasHeader] = useState(false);
    const color = league.color || CATEGORY_META[league.category]?.color || "#9D4CDD";
    const invalidate = () => qc.invalidateQueries({ queryKey: ["league-teams", league.id] });

    const addOne = useMutation({
        mutationFn: () => addLeagueTeam(league.id, one),
        onSuccess: () => { invalidate(); setOne({ name: "", abbr: "", city: "" }); setMode(null); toast.success("Team added"); },
        onError: () => toast.error("Could not add team"),
    });
    const addBulk = useMutation({
        mutationFn: () => bulkAddLeagueTeams(league.id, { tsv, has_header: hasHeader }),
        onSuccess: (r) => { invalidate(); setTsv(""); setMode(null); toast.success(`Added ${r.added} team${r.added === 1 ? "" : "s"}`); },
        onError: () => toast.error("Bulk add failed"),
    });
    const delTeam = useMutation({
        mutationFn: (tid) => deleteLeagueTeam(league.id, tid),
        onSuccess: () => { invalidate(); },
    });

    return (
        <Modal title={`${league.abbr} — Teams`} onClose={onClose} testId="league-panel" wide>
            <div className="flex items-start gap-3 mb-4">
                <div className="h-12 w-12 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}1f`, border: `1px solid ${color}55` }}>
                    <span className="font-black" style={{ color, fontSize: monogram(league.abbr).length > 3 ? "10px" : "13px" }}>{monogram(league.abbr)}</span>
                </div>
                <div className="min-w-0">
                    <p className="text-sm font-bold text-white leading-tight">{league.name}</p>
                    <p className="text-[11px] text-white/45">{[CATEGORY_META[league.category]?.label, league.country, league.level].filter(Boolean).join(" · ")}</p>
                    {league.link && <a href={league.link} target="_blank" rel="noreferrer" data-testid="league-external-link" className="inline-flex items-center gap-1 text-[11px] text-[#C4A5F4] hover:underline mt-1">Official site <ExternalLink className="h-3 w-3" /></a>}
                </div>
                {adminOn && <button onClick={() => onEdit(league)} data-testid="league-panel-edit" className="ml-auto shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold text-white/70 hover:text-white bg-white/5 hover:bg-white/10 inline-flex items-center gap-1.5"><Pencil className="h-3.5 w-3.5" /> Edit</button>}
            </div>

            {adminOn && (
                <div className="flex flex-wrap gap-2 mb-3">
                    <button onClick={() => setMode(mode === "one" ? null : "one")} data-testid="team-add-one-toggle" className="px-3 py-1.5 rounded-lg text-xs font-bold bg-[#9D4CDD]/15 text-[#C4A5F4] hover:bg-[#9D4CDD]/25 inline-flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> Add team</button>
                    <button onClick={() => setMode(mode === "bulk" ? null : "bulk")} data-testid="team-add-bulk-toggle" className="px-3 py-1.5 rounded-lg text-xs font-bold bg-white/5 text-white/70 hover:bg-white/10 inline-flex items-center gap-1.5"><ClipboardPaste className="h-3.5 w-3.5" /> Bulk paste teams</button>
                </div>
            )}

            {adminOn && mode === "one" && (
                <div className="rounded-xl border border-white/10 bg-[#0E0F16] p-3 mb-4 grid grid-cols-3 gap-2">
                    <input value={one.name} onChange={(e) => setOne({ ...one, name: e.target.value })} placeholder="Team name *" data-testid="team-one-name" className={`${inp} col-span-3`} />
                    <input value={one.abbr} onChange={(e) => setOne({ ...one, abbr: e.target.value })} placeholder="Abbr" data-testid="team-one-abbr" className={inp} />
                    <input value={one.city} onChange={(e) => setOne({ ...one, city: e.target.value })} placeholder="City" data-testid="team-one-city" className={`${inp} col-span-2`} />
                    <button onClick={() => addOne.mutate()} disabled={!one.name.trim() || addOne.isPending} data-testid="team-one-submit" className="col-span-3 px-3 py-2 rounded-lg text-sm font-bold bg-[#9D4CDD] text-white hover:bg-[#8B3FC9] disabled:opacity-40">Add team</button>
                </div>
            )}

            {adminOn && mode === "bulk" && (
                <div className="rounded-xl border border-white/10 bg-[#0E0F16] p-3 mb-4">
                    <p className="text-[11px] text-white/50 mb-2">Paste from Google Sheets. Columns: <span className="text-white/75 font-semibold">name · abbr · city · logo_url · link</span>. Only name is required.</p>
                    <textarea value={tsv} onChange={(e) => setTsv(e.target.value)} rows={6} placeholder={"Toronto Sceptres\tTOR\tToronto\nMontreal Victoire\tMTL\tMontreal"} data-testid="team-bulk-tsv" className={`${inp} w-full font-mono text-xs resize-y`} />
                    <div className="flex items-center gap-3 mt-2">
                        <label className="flex items-center gap-2 text-xs text-white/70"><input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} data-testid="team-bulk-header" /> First row is a header</label>
                        <button onClick={() => addBulk.mutate()} disabled={!tsv.trim() || addBulk.isPending} data-testid="team-bulk-submit" className="ml-auto px-3 py-2 rounded-lg text-sm font-bold bg-[#9D4CDD] text-white hover:bg-[#8B3FC9] disabled:opacity-40">Add teams</button>
                    </div>
                </div>
            )}

            <div className="rounded-xl border border-white/[0.06] bg-[#0E0F16] max-h-[46vh] overflow-y-auto" data-testid="league-teams-list">
                {isLoading ? <p className="p-4 text-xs text-white/40">Loading teams…</p>
                    : teams.length === 0 ? <p className="p-4 text-xs text-white/40">No teams yet{adminOn ? " — add or bulk-paste some above." : "."}</p>
                        : teams.map((t) => (
                            <div key={t.id} data-testid={`team-row-${t.id}`} className="flex items-center gap-3 px-4 py-2.5 border-b border-white/[0.04] last:border-0">
                                <span className="text-sm text-white/90 font-medium">{t.name}</span>
                                {t.abbr && <span className="text-[10px] font-bold text-white/40 uppercase">{t.abbr}</span>}
                                {t.city && <span className="text-[11px] text-white/35">{t.city}</span>}
                                {adminOn && <button onClick={() => delTeam.mutate(t.id)} data-testid={`team-delete-${t.id}`} className="ml-auto h-7 w-7 rounded-lg flex items-center justify-center text-white/30 hover:text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></button>}
                            </div>
                        ))}
            </div>
        </Modal>
    );
}

export default function Leagues() {
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("all");
    const [open, setOpen] = useState(() => Object.fromEntries(CATEGORY_ORDER.map((c) => [c, true])));
    const [panel, setPanel] = useState(null);       // league open in teams panel
    const [editLeague, setEditLeague] = useState(undefined); // undefined=closed, null=add, obj=edit
    const [bulkOpen, setBulkOpen] = useState(false);

    const { isAdmin, isOwner } = useAuth();
    const { adminMode } = useAdminMode();
    const { setLeague } = useLeague();
    const adminOn = isAdmin && adminMode;
    const qc = useQueryClient();

    const { data: leagues = [] } = useQuery({ queryKey: ["leagues-directory"], queryFn: getLeaguesDirectory });

    const dragId = React.useRef(null);
    const reorder = useMutation({
        mutationFn: (order) => reorderLeagues(order),
        onError: () => { qc.invalidateQueries({ queryKey: ["leagues-directory"] }); toast.error("Reorder failed"); },
    });
    const onDragStart = (id) => { dragId.current = id; };
    const onDrop = (targetId) => {
        const src = dragId.current;
        dragId.current = null;
        if (!src || src === targetId) return;
        const ids = leagues.map((l) => l.id);
        const from = ids.indexOf(src), to = ids.indexOf(targetId);
        if (from < 0 || to < 0) return;
        ids.splice(to, 0, ids.splice(from, 1)[0]);
        const byId = Object.fromEntries(leagues.map((l) => [l.id, l]));
        const next = ids.map((id, i) => ({ ...byId[id], order: i }));
        qc.setQueryData(["leagues-directory"], next); // optimistic
        reorder.mutate(ids);
    };

    const counts = useMemo(() => {
        const c = { professional: 0, college: 0, international: 0, historical: 0 };
        leagues.forEach((l) => { c[l.category] = (c[l.category] || 0) + 1; });
        return c;
    }, [leagues]);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        const map = {};
        CATEGORY_ORDER.forEach((c) => { map[c] = []; });
        leagues.forEach((l) => {
            if (cat !== "all" && l.category !== cat) return;
            if (needle && !`${l.abbr} ${l.name} ${l.country}`.toLowerCase().includes(needle)) return;
            (map[l.category] || (map[l.category] = [])).push(l);
        });
        return map;
    }, [q, cat, leagues]);

    const visibleCats = cat === "all" ? CATEGORY_ORDER : [cat];

    return (
        <div className="max-w-[1180px] mx-auto px-4 md:px-8 py-8" data-testid="leagues-page">
            {/* Change League — the league/team switcher nav, moved here from the top nav bar */}
            <div className="mb-6 rounded-2xl border border-[#9D4CDD]/30 bg-[#0B0C12] px-4 py-4" data-testid="leagues-change-league-nav">
                <p className="text-[11px] uppercase tracking-[0.15em] font-black text-[#C4A5F4] mb-3 inline-flex items-center gap-1.5"><Globe className="h-3.5 w-3.5" /> Change League</p>
                <FindCascade onSelectLeague={setLeague} isOwner={isOwner} isAdmin={isAdmin} />
            </div>
            {/* Browse bar (moved above the category breakdown) */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6 flex-wrap">
                <h2 className="font-heading text-3xl sm:text-4xl font-black text-white tracking-tight mr-auto">Leagues</h2>
                <div className="relative">
                    <Search className="h-4 w-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leagues..." data-testid="leagues-search" className={`${inp} pl-9 w-full sm:w-64`} />
                </div>
                <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="leagues-category" className={inp}>
                    <option value="all">All Categories</option>
                    {CATEGORY_ORDER.map((c) => <option key={c} value={c}>{CATEGORY_META[c].label}</option>)}
                </select>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8" data-testid="leagues-stats">
                <StatCard Icon={Globe} value={leagues.length} label="Total Leagues" sub="Across 4 categories" />
                <StatCard Icon={Trophy} value={counts.professional} label="Professional Leagues" sub="Top-level competition" />
                <StatCard Icon={GraduationCap} value={counts.college} label="College Leagues" sub="NCAA & U SPORTS" />
                <StatCard Icon={Globe} value={counts.international} label="International Leagues" sub="National-team events" />
                <StatCard Icon={Clock} value={counts.historical} label="Historical Leagues" sub="Past competitions" />
            </div>

            {adminOn && (
                <div className="flex flex-wrap items-center gap-2 mb-6 rounded-2xl border border-[#9D4CDD]/30 bg-[#9D4CDD]/[0.06] px-4 py-3" data-testid="leagues-admin-bar">
                    <span className="text-[11px] uppercase tracking-wider font-black text-[#C4A5F4] mr-2 inline-flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" /> Admin</span>
                    <button onClick={() => setEditLeague(null)} data-testid="leagues-add-btn" className="px-3 py-1.5 rounded-lg text-xs font-bold bg-[#9D4CDD] text-white hover:bg-[#8B3FC9] inline-flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> Add league</button>
                    <button onClick={() => setBulkOpen(true)} data-testid="leagues-bulk-btn" className="px-3 py-1.5 rounded-lg text-xs font-bold bg-white/5 text-white/80 hover:bg-white/10 inline-flex items-center gap-1.5"><ClipboardPaste className="h-3.5 w-3.5" /> Bulk paste leagues</button>
                    <span className="text-[11px] text-white/40 ml-auto inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Drag to reorder · click to manage teams</span>
                </div>
            )}

            <div className="space-y-4">
                {visibleCats.map((c) => (
                    <Section key={c} cat={c} leagues={filtered[c]} open={!!open[c]} onToggle={() => setOpen((o) => ({ ...o, [c]: !o[c] }))}
                        adminOn={adminOn} onOpen={setPanel} onDragStart={onDragStart} onDrop={onDrop} />
                ))}
            </div>

            <div className="mt-8 flex flex-col md:flex-row md:items-center gap-4 rounded-2xl bg-[#0B0C12] border border-white/[0.06] px-5 py-4">
                <span className="h-9 w-9 rounded-full flex items-center justify-center bg-[#9D4CDD]/12 text-[#C4A5F4] shrink-0"><Info className="h-4 w-4" /></span>
                <div className="mr-auto">
                    <p className="text-sm font-bold text-white">Don't see a league?</p>
                    <p className="text-[11px] text-white/45">Help us keep our league directory up to date.</p>
                </div>
                <span className="text-[11px] text-white/40">Data sourced from official league sites &amp; Elite Prospects.</span>
            </div>

            {panel && <LeaguePanel league={panel} adminOn={adminOn} onEdit={(lg) => { setPanel(null); setEditLeague(lg); }} onClose={() => setPanel(null)} />}
            {editLeague !== undefined && <LeagueEditModal league={editLeague} onClose={() => setEditLeague(undefined)} />}
            {bulkOpen && <BulkLeaguesModal onClose={() => setBulkOpen(false)} />}
        </div>
    );
}
